import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { SUPPORTED_CURRENCIES, convertToOMR, getExchangeRates } from '../../../services/exchangeRateService';

const MaterialsGrid = ({ data, onUpdate, onDelete, searchQuery }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupBy, setGroupBy] = useState('category');
  const [rateInfo, setRateInfo] = useState({ date: '', source: '' });
  const [convertingId, setConvertingId] = useState(null);

  // Load rate info on mount for display
  useEffect(() => {
    getExchangeRates()?.then(({ date, source }) => {
      setRateInfo({ date, source });
    })?.catch(() => {});
  }, []);

  const categoryOptions = [
    { value: 'fiber', label: 'Fiber Optic' },
    { value: 'cable', label: 'Cable' },
    { value: 'hardware', label: 'Hardware' },
    { value: 'equipment', label: 'Equipment' },
    { value: 'consumables', label: 'Consumables' }
  ];

  const uomOptions = [
    { value: 'meter', label: 'Meter' },
    { value: 'piece', label: 'Piece' },
    { value: 'unit', label: 'Unit' },
    { value: 'box', label: 'Box' },
    { value: 'roll', label: 'Roll' }
  ];

  const groupByOptions = [
    { value: 'category', label: 'Group by Category' },
    { value: 'uom', label: 'Group by UOM' },
    { value: 'none', label: 'No Grouping' }
  ];

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item =>
      item?.itemName?.toLowerCase()?.includes(query) ||
      item?.itemCode?.toLowerCase()?.includes(query) ||
      item?.category?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  const groupedData = useMemo(() => {
    if (groupBy === 'none') return { 'All Items': filteredData };
    const groups = {};
    filteredData?.forEach(item => {
      const groupKey = item?.[groupBy] || 'Other';
      if (!groups?.[groupKey]) groups[groupKey] = [];
      groups?.[groupKey]?.push(item);
    });
    return groups;
  }, [filteredData, groupBy]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev?.[groupName] }));
  };

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData({
      ...item,
      currency: item?.currency || 'OMR',
      unitCostOriginal: item?.unitCostOriginal ?? item?.unitCost,
      unitCostOmr: item?.unitCostOmr ?? item?.unitCost,
      exchangeRate: item?.exchangeRate ?? 1,
      exchangeRateDate: item?.exchangeRateDate || new Date()?.toISOString()?.split('T')?.[0],
    });
  };

  // When currency or original cost changes in edit mode, auto-convert
  const handleCurrencyOrCostChange = useCallback(async (newCurrency, newOriginalCost) => {
    const currency = newCurrency ?? editData?.currency ?? 'OMR';
    const originalCost = parseFloat(newOriginalCost ?? editData?.unitCostOriginal) || 0;

    if (currency === 'OMR') {
      setEditData(prev => ({
        ...prev,
        currency,
        unitCostOriginal: originalCost,
        unitCost: originalCost,
        unitCostOmr: originalCost,
        exchangeRate: 1,
        exchangeRateDate: new Date()?.toISOString()?.split('T')?.[0],
      }));
      return;
    }

    setConvertingId('edit');
    try {
      const { omrAmount, rate, date } = await convertToOMR(originalCost, currency);
      setEditData(prev => ({
        ...prev,
        currency,
        unitCostOriginal: originalCost,
        unitCost: omrAmount,
        unitCostOmr: omrAmount,
        exchangeRate: rate,
        exchangeRateDate: date,
      }));
      setRateInfo(prev => ({ ...prev, date }));
    } catch (_) {
      // keep previous values
    } finally {
      setConvertingId(null);
    }
  }, [editData]);

  const handleSave = () => {
    onUpdate(editData);
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === filteredData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData?.map(item => item?.id));
    }
  };

  const formatOMR = (value) => `OMR ${parseFloat(value || 0)?.toFixed(3)}`;

  const getCurrencyLabel = (currency) => {
    if (!currency || currency === 'OMR') return 'OMR';
    return currency;
  };

  const renderRow = (item) => {
    const currency = item?.currency || 'OMR';
    const isNonOMR = currency !== 'OMR';
    const originalCost = item?.unitCostOriginal ?? item?.unitCost;
    const omrCost = item?.unitCostOmr ?? item?.unitCost;
    const effectiveCostOmr = omrCost * (1 + (item?.wastePercentage || 0) / 100);

    return (
      <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
        <td className="px-4 py-3">
          <input
            type="checkbox"
            checked={selectedRows?.includes(item?.id)}
            onChange={() => handleSelectRow(item?.id)}
            className="w-4 h-4 rounded border-border"
          />
        </td>
        <td className="px-4 py-3">
          {editingId === item?.id ? (
            <Input
              type="text"
              value={editData?.itemCode}
              onChange={(e) => setEditData({ ...editData, itemCode: e?.target?.value })}
              className="w-full"
            />
          ) : (
            <span className="text-sm text-foreground font-mono">{item?.itemCode}</span>
          )}
        </td>
        <td className="px-4 py-3">
          {editingId === item?.id ? (
            <Input
              type="text"
              value={editData?.itemName}
              onChange={(e) => setEditData({ ...editData, itemName: e?.target?.value })}
              className="w-full"
            />
          ) : (
            <span className="text-sm text-foreground font-medium">{item?.itemName}</span>
          )}
        </td>
        <td className="px-4 py-3">
          {editingId === item?.id ? (
            <Select
              options={categoryOptions}
              value={editData?.category}
              onChange={(value) => setEditData({ ...editData, category: value })}
            />
          ) : (
            <span className="text-sm text-muted-foreground capitalize">{item?.category}</span>
          )}
        </td>
        <td className="px-4 py-3 text-center">
          {editingId === item?.id ? (
            <Select
              options={uomOptions}
              value={editData?.uom}
              onChange={(value) => setEditData({ ...editData, uom: value })}
            />
          ) : (
            <span className="text-sm text-foreground uppercase">{item?.uom}</span>
          )}
        </td>
        {/* Currency column */}
        <td className="px-4 py-3 text-center">
          {editingId === item?.id ? (
            <Select
              options={SUPPORTED_CURRENCIES}
              value={editData?.currency || 'OMR'}
              onChange={(value) => handleCurrencyOrCostChange(value, editData?.unitCostOriginal)}
            />
          ) : (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isNonOMR ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-muted text-muted-foreground'}`}>
              {currency}
            </span>
          )}
        </td>
        {/* Unit Cost (original currency) */}
        <td className="px-4 py-3 text-right">
          {editingId === item?.id ? (
            <div className="space-y-1">
              <Input
                type="number"
                value={editData?.unitCostOriginal ?? editData?.unitCost}
                onChange={(e) => handleCurrencyOrCostChange(editData?.currency, e?.target?.value)}
                className="w-full text-right"
                placeholder="Original cost"
              />
              {convertingId === 'edit' && (
                <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                  <Icon name="Loader2" size={10} className="animate-spin" />
                  Converting...
                </div>
              )}
            </div>
          ) : (
            <div className="text-right">
              <div className="text-sm text-foreground font-medium">
                {getCurrencyLabel(currency)} {parseFloat(originalCost || 0)?.toFixed(3)}
              </div>
              {isNonOMR && (
                <div className="text-xs text-primary font-semibold mt-0.5">
                  = {formatOMR(omrCost)}
                </div>
              )}
            </div>
          )}
        </td>
        {/* OMR converted cost (shown in edit mode as read-only) */}
        {editingId === item?.id && (
          <td className="px-4 py-3 text-right">
            <div className="text-sm font-semibold text-primary">
              {formatOMR(editData?.unitCostOmr ?? editData?.unitCost)}
            </div>
            {editData?.currency !== 'OMR' && editData?.exchangeRate && (
              <div className="text-xs text-muted-foreground mt-0.5">
                Rate: {parseFloat(editData?.exchangeRate || 1)?.toFixed(6)}
              </div>
            )}
          </td>
        )}
        <td className="px-4 py-3 text-right">
          {editingId === item?.id ? (
            <Input
              type="number"
              value={editData?.wastePercentage}
              onChange={(e) => setEditData({ ...editData, wastePercentage: parseFloat(e?.target?.value) })}
              className="w-full text-right"
            />
          ) : (
            <span className="text-sm text-foreground">{item?.wastePercentage}%</span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          <span className="text-sm text-primary font-semibold">{formatOMR(effectiveCostOmr)}</span>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-center gap-2">
            {editingId === item?.id ? (
              <>
                <Button variant="success" size="xs" iconName="Check" onClick={handleSave} />
                <Button variant="ghost" size="xs" iconName="X" onClick={handleCancel} />
              </>
            ) : (
              <>
                <Button variant="ghost" size="xs" iconName="Edit" onClick={() => handleEdit(item)} />
                <Button variant="ghost" size="xs" iconName="Trash2" onClick={() => onDelete(item?.id)} />
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      {/* Rate info banner */}
      {rateInfo?.date && (
        <div className="mx-4 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
          <Icon name="RefreshCw" size={13} className="text-blue-500 flex-shrink-0" />
          <span className="text-xs text-blue-700 dark:text-blue-300">
            Exchange rates as of <strong>{rateInfo?.date}</strong>
            {rateInfo?.source === 'live' && ' (live)'}
            {rateInfo?.source === 'cache' && ' (cached today)'}
            {rateInfo?.source === 'fallback' && ' (approximate – live rates unavailable)'}
            {' '}· Rates are locked per transaction for audit accuracy.
          </span>
        </div>
      )}
      <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="Layers" size={18} className="text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">View Options</span>
        </div>
        <Select options={groupByOptions} value={groupBy} onChange={setGroupBy} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[1300px]">
          <thead>
            <tr className="bg-muted border-b border-border">
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-border"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Item Code</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Item Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">UOM</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Currency</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Unit Cost</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Waste %</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Effective Cost (OMR)</th>
              <th className="w-32 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedData)?.map(([groupName, items]) => (
              <React.Fragment key={groupName}>
                {groupBy !== 'none' && (
                  <tr className="bg-muted/70 border-y border-border">
                    <td colSpan="10" className="px-4 py-2">
                      <button
                        onClick={() => toggleGroup(groupName)}
                        className="flex items-center gap-2 w-full text-left hover:text-primary transition-smooth"
                      >
                        <Icon
                          name={expandedGroups?.[groupName] ? 'ChevronDown' : 'ChevronRight'}
                          size={18}
                          className="text-muted-foreground"
                        />
                        <span className="text-sm font-semibold text-foreground capitalize">{groupName}</span>
                        <span className="text-xs text-muted-foreground">({items?.length} items)</span>
                      </button>
                    </td>
                  </tr>
                )}
                {(groupBy === 'none' || expandedGroups?.[groupName]) && items?.map(renderRow)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filteredData?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Icon name="FileX" size={48} className="text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground">No materials found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialsGrid;